import { computed, observable, action } from 'mobx';
import useMount from '@src/utils/hooks/use-mount';
import getLanguageByPath from '@src/utils/locale/getLanguageByPath';
import { autobind } from 'core-decorators';
import BaseStore from '@src/utils/store/base-store';

class LanguageSelectStore extends BaseStore {
  constructor(options = {}) {
    super();
    Object.assign(this, options);
  }

  @observable isOpen = false;
  @observable excludedLanguages = [];

  /**
   * 语言列表
   * @type {Object}
   * @property {string} name - 语言名称
   * @property {string} languageUrl - 语言URL
   */
  @observable
  LANGUAGE = {
    en: { name: 'English', languageUrl: '' },
    ja: { name: '日本語', languageUrl: '' },
    ko: { name: '한국어', languageUrl: '' },
    'zh-cn': { name: '', languageUrl: '' },
    'zh-Hans': { name: '', languageUrl: '' },
    'zh-Hant': { name: '繁体中文', languageUrl: '' },
    'zh-tc': { name: '', languageUrl: '' },
    it: { name: '', languageUrl: '' },
    in: { name: '', languageUrl: '' },
    id: { name: '', languageUrl: '' },
    es: { name: 'ESPAÑOL', languageUrl: '' },
    pt: { name: 'PORTUGUÊS', languageUrl: '' },
    de: { name: 'DEUTSCH', languageUrl: '' },
    fr: { name: 'FRANÇAIS', languageUrl: '' },
    ru: { name: 'РУССКИЙ', languageUrl: '' },
    th: { name: 'ภาษาไทย', languageUrl: '' }
  };

  /**
   * Available language list
   * @type {Array<{language: string, languageKey: string, languageUrl: string}>}
   * @description
   * @returns {Array}
   */
  @observable
  languageList = Object.keys(this.LANGUAGE)
    .filter(name => !!this.LANGUAGE[name].name)
    .map(name => ({
      language: this.LANGUAGE[name].name,
      languageKey: name,
      languageUrl: this.LANGUAGE[name].url
    }));

  @computed
  get currentLanguageObject() {
    return this.languageList.find(item => item.languageKey === getLanguageByPath());
  }

  @autobind
  onLanguageBtnMouseEnter() {
    this.isOpen = true;
  }

  @autobind
  onLanguageBtnMouseLeave() {
    this.isOpen = false;
  }

  /**
   * 处理语言选择事件
   * @param {string} lang - 新选择的语言
   * @returns {Promise<void>}
   */
  @action
  async onLanguageSelected(lang) {
    const languageUrl = this.LANGUAGE[lang].languageUrl;
    if (languageUrl) {
      window.location.href = languageUrl;
    } else {
      const currentLanguage = getLanguageByPath();
      const newPath = window.location.pathname.replace(`/${currentLanguage}`, `/${lang}`);
      window.location.href = window.location.href.replace(window.location.pathname, newPath);
    }
  }

  /**
   * 递归检查元素的父节点是否包含指定的id
   * @param {HTMLElement} element - 要检查的DOM元素
   * @param {string} mark - 要匹配的父元素id
   * @returns {boolean} - 如果找到匹配的父元素id则返回true，否则返回false
   */
  @autobind
  checkParentId(element, mark) {
    if (!element?.parentNode) return false;
    const { parentNode: parent } = element;
    return parent.id === mark || this.checkParentId(parent, mark);
  }

  use() {
    super.use();

    useMount(() => {
      const hideExpandList = e => {
        if (!this.checkParentId(e.target, 'languageContainer')) {
          this.isOpen = false;
        }
      };

      window.addEventListener('click', hideExpandList, false);
      return () => window.removeEventListener('click', hideExpandList, false);
    });
  }
}

export default LanguageSelectStore;
